import { createFileRoute, notFound } from "@tanstack/react-router";
import { PageTemplate } from "@/components/site/PageTemplate";
import { industries } from "@/content/industries";
import industryHeroAssetSrc from "@/assets/industry-hero.jpg";
const industryHeroAsset = { url: industryHeroAssetSrc };
import indSmbSrc from "@/assets/ind-small-midsize-businesses.jpg";
const indSmb = { url: indSmbSrc };
import indManufacturingSrc from "@/assets/ind-manufacturing.jpg";
const indManufacturing = { url: indManufacturingSrc };
import indManufacturingCostSrc from "@/assets/ind-manufacturing-cost.jpg";
const indManufacturingCost = { url: indManufacturingCostSrc };
import indSupplyChainSrc from "@/assets/ind-supply-chain-logistics.jpg";
const indSupplyChain = { url: indSupplyChainSrc };
import indHealthcareSrc from "@/assets/ind-healthcare.jpg";
const indHealthcare = { url: indHealthcareSrc };
import indConstructionSrc from "@/assets/ind-construction-engineering.jpg";
const indConstruction = { url: indConstructionSrc };
import indProfessionalSrc from "@/assets/ind-professional-business-services.jpg";
const indProfessional = { url: indProfessionalSrc };
import indMiningSrc from "@/assets/ind-mining-natural-resources.jpg";
const indMining = { url: indMiningSrc };
import indUtilitiesSrc from "@/assets/ind-utilities-energy.jpg";
const indUtilities = { url: indUtilitiesSrc };
import indGovernmentSrc from "@/assets/ind-government-public-sector.jpg";
const indGovernment = { url: indGovernmentSrc };
import smbCostSrc from "@/assets/smb-cost.jpg";
const smbCost = { url: smbCostSrc };
import indManufacturingHeroSrc from "@/assets/ind-manufacturing-hero-4.jpg";
const indManufacturingHero = { url: indManufacturingHeroSrc };
import indSupplyChainHeroSrc from "@/assets/ind-supply-chain-hero.jpg";
const indSupplyChainHero = { url: indSupplyChainHeroSrc };
import indHealthcareHeroSrc from "@/assets/ind-healthcare-hero.jpg";
const indHealthcareHero = { url: indHealthcareHeroSrc };
import healthcareCostSrc from "@/assets/ind-healthcare-cost.jpg";
const healthcareCost = { url: healthcareCostSrc };
import supplyChainCostSrc from "@/assets/supply-chain-cost.jpg";
const supplyChainCost = { url: supplyChainCostSrc };
import indConstructionHeroSrc from "@/assets/ind-construction-hero.jpg";
const indConstructionHero = { url: indConstructionHeroSrc };
import indConstructionCostSrc from "@/assets/ind-construction-cost.jpg";
const indConstructionCost = { url: indConstructionCostSrc };
import indProfessionalHeroSrc from "@/assets/ind-professional-hero.jpg";
const indProfessionalHero = { url: indProfessionalHeroSrc };
import indProfessionalCostSrc from "@/assets/ind-professional-cost.jpg";
const indProfessionalCost = { url: indProfessionalCostSrc };
import indMiningHeroSrc from "@/assets/ind-mining-hero.jpg";
const indMiningHero = { url: indMiningHeroSrc };
import indMiningCostSrc from "@/assets/ind-mining-cost.jpg";
const indMiningCost = { url: indMiningCostSrc };
import indUtilitiesHeroSrc from "@/assets/ind-utilities-hero.jpg";
const indUtilitiesHero = { url: indUtilitiesHeroSrc };
import indUtilitiesCostSrc from "@/assets/ind-utilities-cost.jpg";
const indUtilitiesCost = { url: indUtilitiesCostSrc };
import indGovernmentHeroSrc from "@/assets/ind-government-hero.jpg";
const indGovernmentHero = { url: indGovernmentHeroSrc };
import indGovernmentCostSrc from "@/assets/ind-government-cost.jpg";
const indGovernmentCost = { url: indGovernmentCostSrc };
import indSmbHeroSrc from "@/assets/ind-smb-hero-handshake.jpg";
import { SITE_URL } from "@/lib/seo";
const indSmbHero = { url: indSmbHeroSrc };

const HERO_BACKGROUNDS: Record<string, string> = {
  "small-midsize-businesses": indSmbHero.url,
  manufacturing: indManufacturingHero.url,
  "supply-chain-logistics": indSupplyChainHero.url,
  healthcare: indHealthcareHero.url,
  "construction-engineering": indConstructionHero.url,
  "professional-business-services": indProfessionalHero.url,
  "mining-natural-resources": indMiningHero.url,
  "utilities-energy": indUtilitiesHero.url,
  "government-public-sector": indGovernmentHero.url,
};

const HERO_POSITIONS: Record<string, string> = {
  manufacturing: "center -100px",
  "professional-business-services": "center 42%",
  "utilities-energy": "center 52%",
};

const APPROACH_BACKGROUNDS: Record<string, string> = {
  "small-midsize-businesses": indSmb.url,
  manufacturing: indManufacturing.url,
  "supply-chain-logistics": indSupplyChain.url,
  healthcare: indHealthcare.url,
  "construction-engineering": indConstruction.url,
  "professional-business-services": indProfessional.url,
  "mining-natural-resources": indMining.url,
  "utilities-energy": indUtilities.url,
  "government-public-sector": indGovernment.url,
};

export const Route = createFileRoute("/who-we-help/$slug")({
  loader: ({ params }) => {
    const page = industries.find((p) => p.slug === params.slug);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return { meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }] };
    return {
      links: [{ rel: "canonical", href: `${SITE_URL}/who-we-help/${params.slug}/` }],
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.metaDescription },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.metaDescription },
      ],
    };
  },
  component: IndustryPage,
});

const IMPACT_STATS: Record<
  string,
  { stat: string; body: string; source: string; image?: string; alt?: string }
> = {
  "small-midsize-businesses": {
    stat: "0.5x–2x",
    body: "The cost of replacing one employee, compared to their annual salary — even by a conservative estimate.",
    source: "Gallup, 2019",
    image: smbCost.url,
    alt: "A manager at a desk working through replacement costs on a calculator",
  },
  manufacturing: {
    stat: "1.9M",
    body: "Manufacturing jobs projected to go unfilled in the U.S. by 2033, roughly half the total need.",
    source: "Deloitte & The Manufacturing Institute, 2024",
    image: indManufacturingCost.url,
    alt: "An empty assembly line stretching down a quiet factory floor",
  },
  "supply-chain-logistics": {
    stat: "53%",
    body: "of transportation and warehousing jobs saw a separation in 2025 — the highest turnover ratio of any goods-adjacent sector.",
    source: "U.S. BLS, JOLTS 2025",
    image: supplyChainCost.url,
  },
  healthcare: {
    stat: "$61K",
    body: "Average cost to replace a single bedside RN — $4.75 million a year for the average hospital.",
    source: "NSI Nursing Solutions, 2025",
    image: healthcareCost.url,
  },
  "construction-engineering": {
    stat: "48%",
    body: "of the U.S. construction workforce turned over in 2025 — close to half the industry, in a single year.",
    source: "U.S. BLS, JOLTS 2025",
    image: indConstructionCost.url,
  },
  "professional-business-services": {
    stat: "5.3%",
    body: "Job openings rate in professional and business services — the highest of any sector outside health care and hospitality.",
    source: "U.S. BLS, JOLTS 2025",
    image: indProfessionalCost.url,
    alt: "A team of professionals reviewing work together around a laptop",
  },
  "mining-natural-resources": {
    stat: "50%+",
    body: "of the current U.S. mining workforce will be retired and replaced by 2029.",
    source: "SME, via CEWD, 2025",
    image: indMiningCost.url,
    alt: "Heavy equipment and haul trucks working an open-pit mine",
  },
  "utilities-energy": {
    stat: "76%",
    body: "of energy and utilities employers report a skills gap within their existing workforce.",
    source: "ManpowerGroup, via CEWD, 2025",
    image: indUtilitiesCost.url,
    alt: "Two utility workers in hard hats and safety vests walking through a high-voltage substation",
  },
  "government-public-sector": {
    stat: "63%",
    body: "increase in federal government separations from 2024 to 2025, in a single year.",
    source: "U.S. BLS, JOLTS 2025",
    image: indGovernmentCost.url,
    alt: "A state capitol building with an American flag flying above it",
  },
};

function IndustryPage() {
  const page = Route.useLoaderData();
  const stat = IMPACT_STATS[page.slug];
  return (
    <PageTemplate
      page={page}
      industrySlug={page.slug}
      symptomsVariant="radial"
      heroBackgroundImage={HERO_BACKGROUNDS[page.slug] ?? industryHeroAsset.url}
      {...(HERO_POSITIONS[page.slug] ? { heroBackgroundPosition: HERO_POSITIONS[page.slug] } : {})}
      {...(APPROACH_BACKGROUNDS[page.slug]
        ? {
            approachBackgroundImage: APPROACH_BACKGROUNDS[page.slug],
            finalBackgroundImage: APPROACH_BACKGROUNDS[page.slug],
          }
        : {})}
      {...(stat ? { impactStat: { ...stat, placeholder: !stat.image } } : {})}
    />
  );
}
